// src/services/profileService.js - SECURED
import { API_BASE_URL } from '../utils/constants';

const getAuthHeaders = () => {
  return {
    'Content-Type': 'application/json',
  };
};

const handleResponse = async (response) => {
  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/';
    }
    const error = await response.json();
    throw new Error(error.error || error.detail || 'Request failed');
  }
  return response.json();
};

export const profileService = {
  /**
   * Get list of educational videos
   */
  async getVideos() {
    try {
      const response = await fetch(
        `${API_BASE_URL}/videos/videos/`,
        { method: 'GET', headers: getAuthHeaders(), credentials: 'include' }
      );
      return handleResponse(response);
    } catch (error) {
      console.error('Error fetching videos:', error);
      throw error;
    }
  },

  /**
   * Get count of new videos since last view
   */
  async getNewVideosCount() {
    try {
      const response = await fetch(
        `${API_BASE_URL}/videos/videos/new-count/`,
        { method: 'GET', headers: getAuthHeaders(), credentials: 'include' }
      );
      return handleResponse(response);
    } catch (error) {
      console.error('Error fetching new videos count:', error);
      throw error;
    }
  },

  /**
   * Get video details by ID
   */
  async getVideoDetail(videoId) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/videos/videos/${videoId}/`,
        { method: 'GET', headers: getAuthHeaders(), credentials: 'include' }
      );
      return handleResponse(response);
    } catch (error) {
      console.error('Error fetching video details:', error);
      throw error;
    }
  },

  /**
   * Get list of news articles
   */
  async getNews(params = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (params.category) queryParams.append('category', params.category);
      if (params.featured) queryParams.append('featured', params.featured);
      if (params.search) queryParams.append('search', params.search);

      const response = await fetch(
        `${API_BASE_URL}/news/news/?${queryParams}`,
        { method: 'GET', headers: getAuthHeaders(), credentials: 'include' }
      );
      return handleResponse(response);
    } catch (error) {
      console.error('Error fetching news:', error);
      throw error;
    }
  },

  /**
   * Get count of new news since last view
   */
  async getNewNewsCount() {
    try {
      const response = await fetch(
        `${API_BASE_URL}/news/news-new-count/`,
        { method: 'GET', headers: getAuthHeaders(), credentials: 'include' }
      );
      return handleResponse(response);
    } catch (error) {
      console.error('Error fetching new news count:', error);
      throw error;
    }
  },

  /**
   * Get news article details by slug
   */
  async getNewsDetail(slug) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/news/news/${slug}/`,
        { method: 'GET', headers: getAuthHeaders(), credentials: 'include' }
      );
      return handleResponse(response);
    } catch (error) {
      console.error('Error fetching news details:', error);
      throw error;
    }
  },

  /**
   * Get news categories
   */
  async getNewsCategories() {
    try {
      const response = await fetch(
        `${API_BASE_URL}/news/categories/`,
        { method: 'GET', headers: getAuthHeaders(), credentials: 'include' }
      );
      return handleResponse(response);
    } catch (error) {
      console.error('Error fetching news categories:', error);
      throw error;
    }
  },

  /**
   * Get user profile
   */
  async getUserProfile() {
    try {
      const response = await fetch(
        `${API_BASE_URL}/profile/profile/`,
        { method: 'GET', headers: getAuthHeaders(), credentials: 'include' }
      );
      return handleResponse(response);
    } catch (error) {
      console.error('Error fetching user profile:', error);
      throw error;
    }
  },

  /**
   * Update user profile
   */
  async updateProfile(profileData) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/profile/profile/`,
        {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify(profileData),
          credentials: 'include',
        }
      );
      return handleResponse(response);
    } catch (error) {
      console.error('Error updating profile:', error);
      throw error;
    }
  },

  /**
   * Change password
   */
  async changePassword(passwordData) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/profile/profile/change-password/`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(passwordData),
          credentials: 'include',
        }
      );
      return handleResponse(response);
    } catch (error) {
      console.error('Error changing password:', error);
      throw error;
    }
  },

  /**
   * Get user bank accounts
   */
  async getBankAccounts() {
    try {
      const response = await fetch(
        `${API_BASE_URL}/profile/profile/bank-accounts/`,
        { method: 'GET', headers: getAuthHeaders(), credentials: 'include' }
      );
      return handleResponse(response);
    } catch (error) {
      console.error('Error fetching bank accounts:', error);
      throw error;
    }
  },

  /**
   * Add new bank account
   */
  async addBankAccount(bankData) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/profile/profile/bank-accounts/`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(bankData),
          credentials: 'include',
        }
      );
      return handleResponse(response);
    } catch (error) {
      console.error('Error adding bank account:', error);
      throw error;
    }
  },

  /**
   * Update existing bank account
   */
  async updateBankAccount(accountId, bankData) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/profile/profile/bank-accounts/${accountId}/update/`,
        {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify(bankData),
          credentials: 'include',
        }
      );
      return handleResponse(response);
    } catch (error) {
      console.error('Error updating bank account:', error);
      throw error;
    }
  },

  /**
   * Set default bank account
   */
  async setDefaultBankAccount(accountId) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/profile/profile/bank-accounts/${accountId}/set-default/`,
        { method: 'POST', headers: getAuthHeaders(), credentials: 'include' }
      );
      return handleResponse(response);
    } catch (error) {
      console.error('Error setting default bank account:', error);
      throw error;
    }
  },

  /**
   * Delete bank account
   */
  async deleteBankAccount(accountId) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/profile/profile/bank-accounts/${accountId}/delete/`,
        { method: 'DELETE', headers: getAuthHeaders(), credentials: 'include' }
      );
      return handleResponse(response);
    } catch (error) {
      console.error('Error deleting bank account:', error);
      throw error;
    }
  },

  /**
   * Get user notifications/alerts from investment and plan systems
   */
  async getAlerts(params = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (params.is_read !== undefined) queryParams.append('is_read', params.is_read);
      if (params.type) queryParams.append('type', params.type);

      const [investmentRes, planRes] = await Promise.all([
        fetch(`${API_BASE_URL}/investment/notifications/?${queryParams}`,
          { method: 'GET', headers: getAuthHeaders(), credentials: 'include' }),
        fetch(`${API_BASE_URL}/plan/notifications/?${queryParams}`,
          { method: 'GET', headers: getAuthHeaders(), credentials: 'include' }),
      ]);

      const investmentData = await handleResponse(investmentRes);
      const planData = await handleResponse(planRes);

      console.log('📧 Raw investment data:', investmentData);
      console.log('📧 Raw plan data:', planData);

      const investmentNotifications = Array.isArray(investmentData)
        ? investmentData
        : (investmentData.notifications || []);

      const planNotifications = Array.isArray(planData)
        ? planData
        : (planData.notifications || []);

      const combined = [
        ...investmentNotifications,
        ...planNotifications,
      ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

      console.log('📧 Combined notifications:', combined);
      return combined;
    } catch (error) {
      console.error('Error fetching alerts:', error);
      throw error;
    }
  },

  /**
   * Get wallet adjustment notifications (admin credits/debits)
   */
  async getWalletNotifications(params = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (params.is_read !== undefined) queryParams.append('is_read', params.is_read);

      const response = await fetch(
        `${API_BASE_URL}/users/wallet/notifications/?${queryParams}`,
        { method: 'GET', headers: getAuthHeaders(), credentials: 'include' }
      );
      return handleResponse(response);
    } catch (error) {
      console.error('Error fetching wallet notifications:', error);
      throw error;
    }
  },

  /**
   * Get deposit rejection notifications
   */
  async getDepositNotifications(params = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (params.is_read !== undefined) queryParams.append('is_read', params.is_read);

      const response = await fetch(
        `${API_BASE_URL}/deposit/notifications/?${queryParams}`,
        { method: 'GET', headers: getAuthHeaders(), credentials: 'include' }
      );
      return handleResponse(response);
    } catch (error) {
      console.error('Error fetching deposit notifications:', error);
      throw error;
    }
  },

  /**
   * Get withdrawal rejection notifications
   */
  async getWithdrawalNotifications(params = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (params.is_read !== undefined) queryParams.append('is_read', params.is_read);

      const response = await fetch(
        `${API_BASE_URL}/withdraw/notifications/?${queryParams}`,
        { method: 'GET', headers: getAuthHeaders(), credentials: 'include' }
      );
      return handleResponse(response);
    } catch (error) {
      console.error('Error fetching withdrawal notifications:', error);
      throw error;
    }
  },

  /**
   * Mark investment notification as read
   */
  async markInvestmentNotificationRead(notificationId) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/investment/notifications/${notificationId}/read/`,
        { method: 'POST', headers: getAuthHeaders(), credentials: 'include' }
      );
      return handleResponse(response);
    } catch (error) {
      console.error('Error marking investment notification as read:', error);
      throw error;
    }
  },

  /**
   * Mark plan notification as read
   */
  async markPlanNotificationRead(notificationId) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/plan/notifications/${notificationId}/read/`,
        { method: 'POST', headers: getAuthHeaders(), credentials: 'include' }
      );
      return handleResponse(response);
    } catch (error) {
      console.error('Error marking plan notification as read:', error);
      throw error;
    }
  },

  /**
   * Mark a single wallet notification as read
   */
  async markWalletNotificationRead(notificationId) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/users/wallet/notifications/${notificationId}/read/`,
        { method: 'POST', headers: getAuthHeaders(), credentials: 'include' }
      );
      return handleResponse(response);
    } catch (error) {
      console.error('Error marking wallet notification as read:', error);
      throw error;
    }
  },

  /**
   * Mark a single deposit notification as read
   */
  async markDepositNotificationRead(notificationId) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/deposit/notifications/${notificationId}/read/`,
        { method: 'POST', headers: getAuthHeaders(), credentials: 'include' }
      );
      return handleResponse(response);
    } catch (error) {
      console.error('Error marking deposit notification as read:', error);
      throw error;
    }
  },

  /**
   * Mark a single withdrawal notification as read
   */
  async markWithdrawalNotificationRead(notificationId) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/withdraw/notifications/${notificationId}/read/`,
        { method: 'POST', headers: getAuthHeaders(), credentials: 'include' }
      );
      return handleResponse(response);
    } catch (error) {
      console.error('Error marking withdrawal notification as read:', error);
      throw error;
    }
  },

  /**
   * Mark all notifications as read (investment, plan, wallet, deposit, withdrawal)
   */
  async markAllNotificationsRead() {
    try {
      await Promise.all([
        fetch(`${API_BASE_URL}/investment/notifications/read-all/`,
          { method: 'POST', headers: getAuthHeaders(), credentials: 'include' }),
        fetch(`${API_BASE_URL}/plan/notifications/read-all/`,
          { method: 'POST', headers: getAuthHeaders(), credentials: 'include' }),
        fetch(`${API_BASE_URL}/users/wallet/notifications/read-all/`,
          { method: 'POST', headers: getAuthHeaders(), credentials: 'include' }),
        fetch(`${API_BASE_URL}/deposit/notifications/read-all/`,
          { method: 'POST', headers: getAuthHeaders(), credentials: 'include' }),
        fetch(`${API_BASE_URL}/withdraw/notifications/read-all/`,
          { method: 'POST', headers: getAuthHeaders(), credentials: 'include' }),
      ]);
      return { message: 'All notifications marked as read' };
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      throw error;
    }
  },
};